import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Trophy, AlertTriangle, TrendingUp, Clock, ClipboardCheck, Award, X, Star } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

const PERIODI = [
  { v: 7, label: "7gg", isSemantic: false },
  { v: 30, label: "30gg", isSemantic: false },
  { v: 90, label: "90gg", isSemantic: false },
  { v: "year_current", label: "Anno corrente", isSemantic: true },
  { v: "year_previous", label: "Anno precedente", isSemantic: true },
];

function ColorBadge({ colore, value }) {
  const m = {
    verde: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    giallo: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    rosso: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${m[colore] || "bg-slate-200 text-slate-700"}`}>{value}</span>;
}

function MedalIcon({ pos }) {
  const m = { 0: "text-yellow-500", 1: "text-slate-400", 2: "text-amber-700" };
  return <Award className={`h-5 w-5 ${m[pos] || "text-muted-foreground"}`} />;
}

function WeekendBadge({ count, title }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid="weekend-badge"
      title={title || `Ha fatto ${count} controlli extra nei weekend del periodo`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300/40 dark:border-amber-700/40"
    >
      <Star className="h-2.5 w-2.5" fill="currentColor" /> Weekend: {count}
    </span>
  );
}

export default function Performance() {
  const { user } = useAuth();
  const [periodo, setPeriodo] = useState(30);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    setLoading(true);
    const qs = typeof periodo === "string" ? `period=${periodo}` : `periodo=${periodo}`;
    api.get(`/rm-performance?${qs}`).then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, [periodo]);

  const top3 = items.slice(0, 3);
  const lowItems = items.filter((x) => x.snapshot.colore !== "verde").slice(-3).reverse();

  // KPIs aggregati
  const total = items.length;
  const taskAss = items.reduce((a, x) => a + x.snapshot.task_assegnate, 0);
  const taskComp = items.reduce((a, x) => a + x.snapshot.task_completate, 0);
  const taskRit = items.reduce((a, x) => a + x.snapshot.task_in_ritardo, 0);
  const completion = taskAss ? Math.round((taskComp / taskAss) * 100) : 0;
  const qualMedia = total ? Math.round(items.reduce((a, x) => a + x.snapshot.qualita_compilazione_pct, 0) / total) : 0;
  const perfMedia = total ? Math.round(items.reduce((a, x) => a + x.snapshot.punteggio_performance, 0) / total) : 0;

  const openDrawer = (rm_id) => {
    const qs = typeof periodo === "string" ? `period=${periodo}` : `periodo=${periodo}`;
    api.get(`/rm-performance/${rm_id}?${qs}`).then((r) => setDrawer(r.data));
  };

  if (user && !["super_admin", "capo_area", "resident_manager"].includes(user.role)) {
    return <div className="text-sm text-muted-foreground">Pagina non disponibile per il tuo ruolo.</div>;
  }

  return (
    <div className="space-y-6 ih-anim-in" data-testid="performance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Performance</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Resident Manager · <span className="font-medium">classifica e KPI</span></h1>
        </div>
        <div className="flex gap-1 ih-glass rounded-full p-1" data-testid="periodo-selector">
          {PERIODI.map((p) => (
            <button key={p.v} data-testid={`periodo-${p.v}`} onClick={() => setPeriodo(p.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${periodo === p.v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> Task completate</div>
          <div className="font-display text-3xl font-medium">{completion}%</div>
          <div className="text-[11px] text-muted-foreground mt-1">{taskComp} / {taskAss}</div>
        </div>
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Task in ritardo</div>
          <div className="font-display text-3xl font-medium">{taskRit}</div>
          <div className="text-[11px] text-muted-foreground mt-1">su {taskAss} totali</div>
        </div>
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Qualità media</div>
          <div className="font-display text-3xl font-medium">{qualMedia}%</div>
          <div className="text-[11px] text-muted-foreground mt-1">perfetto + buono</div>
        </div>
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><Award className="h-3 w-3" /> Performance media</div>
          <div className="font-display text-3xl font-medium">{perfMedia}<span className="text-base text-muted-foreground font-normal">/100</span></div>
          <div className="text-[11px] text-muted-foreground mt-1">{total} RM monitorati</div>
        </div>
      </div>

      {/* Classifica */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ih-glass rounded-2xl p-6" data-testid="top-rm">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <h2 className="font-display text-lg tracking-tight">Top 3 Resident Manager</h2>
          </div>
          {top3.length === 0 ? <p className="text-sm text-muted-foreground">Nessun dato.</p> : (
            <div className="space-y-2">
              {top3.map((x, i) => (
                <div key={x.resident_manager.id} onClick={() => openDrawer(x.resident_manager.id)} data-testid={`top-rm-${i}`} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-4 transition-colors">
                  <MedalIcon pos={i} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">{x.resident_manager.nome} {x.resident_manager.cognome}</span>
                      <WeekendBadge count={x.snapshot.weekend_extra?.controlli_completati} />
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{x.hotel?.nome || "—"}</div>
                  </div>
                  <ColorBadge colore={x.snapshot.colore} value={`${x.snapshot.punteggio_performance}/100`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ih-glass rounded-2xl p-6" data-testid="improve-rm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
            <h2 className="font-display text-lg tracking-tight">Performance da migliorare</h2>
          </div>
          {lowItems.length === 0 ? <p className="text-sm text-muted-foreground">Nessun RM critico — tutto in verde!</p> : (
            <div className="space-y-2">
              {lowItems.map((x) => (
                <div key={x.resident_manager.id} onClick={() => openDrawer(x.resident_manager.id)} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer flex items-center gap-4 transition-colors">
                  <div className="h-9 w-9 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-xs font-bold text-rose-700 dark:text-rose-300">{x.resident_manager.nome[0]}{x.resident_manager.cognome[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">{x.resident_manager.nome} {x.resident_manager.cognome}</span>
                      <WeekendBadge count={x.snapshot.weekend_extra?.controlli_completati} />
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{x.hotel?.nome || "—"}</div>
                  </div>
                  <ColorBadge colore={x.snapshot.colore} value={`${x.snapshot.punteggio_performance}/100`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabella completa */}
      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-lg tracking-tight mb-4">Tutti i Resident Manager</h2>
        {loading ? (
          <div className="space-y-2" data-testid="perf-skeleton">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 rounded-xl bg-muted/40 animate-pulse" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rm-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border/60">
                  <th className="py-2 pr-2">RM</th>
                  <th className="py-2 pr-2">Hotel</th>
                  <th className="py-2 pr-2 text-right">Ass.</th>
                  <th className="py-2 pr-2 text-right">Comp.</th>
                  <th className="py-2 pr-2 text-right">Ritardo</th>
                  <th className="py-2 pr-2 text-right">Non eseg.</th>
                  <th className="py-2 pr-2 text-right">Qualità</th>
                  <th className="py-2 pr-2 text-right">Punteggio</th>
                </tr>
              </thead>
              <tbody>
                {items.map((x) => (
                  <tr key={x.resident_manager.id} onClick={() => openDrawer(x.resident_manager.id)} className="border-b border-border/40 hover:bg-accent/30 cursor-pointer">
                    <td className="py-3 pr-2 flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-lavender-200 dark:bg-lavender-900/40 flex items-center justify-center text-[11px] font-bold">{x.resident_manager.nome[0]}{x.resident_manager.cognome[0]}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-2">
                          <span className="truncate">{x.resident_manager.nome} {x.resident_manager.cognome}</span>
                          <WeekendBadge count={x.snapshot.weekend_extra?.controlli_completati} />
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{x.resident_manager.email}</div>
                      </div>
                    </td>
                    <td className="py-3 pr-2 text-muted-foreground">{x.hotel?.nome || "—"}</td>
                    <td className="py-3 pr-2 text-right">{x.snapshot.task_assegnate}</td>
                    <td className="py-3 pr-2 text-right text-emerald-600 dark:text-emerald-400">{x.snapshot.task_completate}</td>
                    <td className="py-3 pr-2 text-right text-rose-600 dark:text-rose-400">{x.snapshot.task_in_ritardo}</td>
                    <td className="py-3 pr-2 text-right text-muted-foreground">{x.snapshot.task_non_eseguite}</td>
                    <td className="py-3 pr-2 text-right">{x.snapshot.qualita_compilazione_pct}%</td>
                    <td className="py-3 pr-2 text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <ColorBadge colore={x.snapshot.colore} value={x.snapshot.punteggio_performance} />
                        {x.snapshot.delta_periodo_pct != null && (
                          <span
                            data-testid={`delta-${x.resident_manager.id}`}
                            className={`text-[10px] tabular-nums ${x.snapshot.delta_periodo_pct > 0 ? "text-emerald-600" : x.snapshot.delta_periodo_pct < 0 ? "text-rose-600" : "text-muted-foreground"}`}
                            title="Delta vs periodo precedente"
                          >
                            {x.snapshot.delta_periodo_pct > 0 ? "▲ +" : x.snapshot.delta_periodo_pct < 0 ? "▼ " : "◆ "}{x.snapshot.delta_periodo_pct}% vs prec.
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" onClick={() => setDrawer(null)} data-testid="rm-drawer">
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:w-[440px] h-full bg-background border-l border-border ih-anim-in overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Dettaglio RM</div>
                  <h2 className="font-display text-2xl tracking-tight">{drawer.resident_manager.nome} {drawer.resident_manager.cognome}</h2>
                  <div className="text-sm text-muted-foreground">{drawer.hotel?.nome}</div>
                </div>
                <button onClick={() => setDrawer(null)} data-testid="drawer-close" className="h-9 w-9 rounded-lg hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-display text-5xl tracking-tight font-medium">{drawer.snapshot.punteggio_performance}</span>
                  <span className="text-sm text-muted-foreground">/100</span>
                  {drawer.snapshot.delta_periodo_pct != null && (
                    <span
                      data-testid="drawer-delta"
                      className={`text-xs font-medium tabular-nums ${drawer.snapshot.delta_periodo_pct > 0 ? "text-emerald-600" : drawer.snapshot.delta_periodo_pct < 0 ? "text-rose-600" : "text-muted-foreground"}`}
                      title="Delta vs periodo precedente"
                    >
                      {drawer.snapshot.delta_periodo_pct > 0 ? "+" : ""}{drawer.snapshot.delta_periodo_pct}% vs prec.
                    </span>
                  )}
                  <ColorBadge colore={drawer.snapshot.colore} value={drawer.snapshot.colore} />
                </div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">finestra {drawer.snapshot.periodo_giorni}gg</div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-6">
                <div className="rounded-lg border border-border/60 p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Task tot.</div><div className="font-display text-xl">{drawer.snapshot.task_assegnate}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Completate</div><div className="font-display text-xl text-emerald-600 dark:text-emerald-400">{drawer.snapshot.task_completate}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Ritardo</div><div className="font-display text-xl text-rose-600 dark:text-rose-400">{drawer.snapshot.task_in_ritardo}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Camere ctrl</div><div className="font-display text-xl">{drawer.snapshot.controlli_camere_effettuati}/{drawer.snapshot.controlli_camere_attesi}</div></div>
              </div>

              {drawer.snapshot.weekend_extra && (drawer.snapshot.weekend_extra.controlli_totali || 0) > 0 && (
                <div
                  data-testid="drawer-weekend-extra"
                  className="rounded-xl border border-amber-300/40 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-950/20 p-3 mb-6 flex items-center gap-3"
                >
                  <Star className="h-4 w-4 text-amber-500 flex-shrink-0" fill="currentColor" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">Controlli Extra Weekend</div>
                    <div className="text-sm">
                      <span className="font-semibold tabular-nums">{drawer.snapshot.weekend_extra.controlli_completati}</span>
                      <span className="text-muted-foreground"> / {drawer.snapshot.weekend_extra.controlli_totali}</span>
                      {drawer.snapshot.weekend_extra.punteggio_medio != null && (
                        <> · media <span className="font-medium tabular-nums">{drawer.snapshot.weekend_extra.punteggio_medio}/100</span></>
                      )}
                      <span className="text-muted-foreground"> · {drawer.snapshot.weekend_extra.giorni_coperti} giorni</span>
                    </div>
                  </div>
                </div>
              )}

              <h3 className="text-sm font-medium mb-3">Trend giornaliero</h3>
              <div className="h-[200px] mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={drawer.breakdown_giornaliero}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="data" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Line type="monotone" dataKey="completate" stroke="#10b981" strokeWidth={2} dot={false} name="Completate" />
                    <Line type="monotone" dataKey="non_eseguite" stroke="#f43f5e" strokeWidth={2} dot={false} name="Non eseguite" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <h3 className="text-sm font-medium mb-2">Breakdown</h3>
              <div className="max-h-[240px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground uppercase text-[10px] tracking-wider">
                    <tr><th className="text-left py-1">Data</th><th className="text-right py-1">Ass.</th><th className="text-right py-1">Comp.</th><th className="text-right py-1">Rit.</th></tr>
                  </thead>
                  <tbody>
                    {drawer.breakdown_giornaliero.slice(-15).reverse().map((d) => (
                      <tr key={d.data} className="border-b border-border/30"><td className="py-1.5">{d.data}</td><td className="text-right">{d.assegnate}</td><td className="text-right text-emerald-600">{d.completate}</td><td className="text-right text-rose-500">{d.in_ritardo}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
